import {
    Breadcrumb,
    DateField,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, FileText } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { ThreeWayMatchBadge } from '../documents/invoices/ThreeWayMatchBadge';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { VendorBill } from '../types';
import { VendorBillActions } from './VendorBillActions';
const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/vendor_bills">Vendor Bills</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight text-foreground">{record.reference}</span>
        </>
    );
};

const VendorBillShowContent = () => {
    const record = useRecordContext<VendorBill>();
    if (!record) return null;

    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mt-4">
            <div className="flex items-center gap-4 border-b pb-4 flex-wrap">
                <StatusBadge status={record.status} />
                <span className="text-slate-300">|</span>
                <ThreeWayMatchBadge resource="vendor_bills" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="pb-3 border-b border-border">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Bill Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Vendor</span>
                            <span className="font-semibold text-foreground">
                                <ReferenceField source="vendor_id" reference="vendors" link="show">
                                    <TextField source="name" className="text-blue-600 hover:underline" />
                                </ReferenceField>
                            </span>
                        </div>

                        {record.contact_person && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Contact Person</span>
                                <span className="text-sm text-foreground">{record.contact_person}</span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Bill Date</span>
                                <span className="font-medium text-foreground"><DateField source="bill_date" /></span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Due Date</span>
                                <span className="font-medium text-foreground"><DateField source="due_date" /></span>
                            </div>
                        </div>

                        {(record.po_id || record.grn_id) && (
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                                {record.po_id && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Linked PO</span>
                                        <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                            <TextField source="reference" className="text-foreground font-mono text-sm" />
                                        </ReferenceField>
                                    </div>
                                )}
                                {record.grn_id && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Linked GRN</span>
                                        <ReferenceField source="grn_id" reference="goods_receipts" link="show">
                                            <TextField source="reference" className="text-foreground font-mono text-sm" />
                                        </ReferenceField>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {record.notes && (
                            <div className="flex flex-col gap-1 pt-3 border-t border-border">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Notes</span>
                                <span className="text-sm text-foreground whitespace-pre-wrap">{record.notes}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3 border-b border-border">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <DollarSign className="w-4 h-4" /> Amounts
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                        <div className="flex items-center justify-between py-2 border-b border-border">
                            <span className="text-muted-foreground font-medium">Items Total</span>
                            <span className="font-medium">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.items_total || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-border">
                            <span className="text-muted-foreground font-medium">Tax Amount</span>
                            <span className="font-medium">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.tax_amount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 font-bold text-lg text-foreground bg-muted rounded-lg px-3 mt-2">
                            <span>Total Amount</span>
                            <span className="text-rose-700">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.total_amount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 font-bold text-lg text-foreground bg-emerald-50 rounded-lg px-3">
                            <span>Amount Paid</span>
                            <span className="text-emerald-700">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.amount_paid || 0)}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {record.items && record.items.length > 0 && (
                <Card>
                    <CardHeader className="pb-3 border-b border-border">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Line Items
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest border-b border-border">
                                    <th className="py-2 pr-2">Description</th>
                                    <th className="py-2 px-2 text-right">Qty</th>
                                    <th className="py-2 px-2 text-right">Price</th>
                                    <th className="py-2 px-2 text-right">GST %</th>
                                    <th className="py-2 pl-2 text-right">Line Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {record.items.map((item: any) => (
                                    <tr key={item.id} className="border-b border-slate-50 last:border-0">
                                        <td className="py-2 pr-2 text-foreground">{item.item_description}</td>
                                        <td className="py-2 px-2 text-right text-muted-foreground">{item.quantity}</td>
                                        <td className="py-2 px-2 text-right text-muted-foreground">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.unit_price || 0)}</td>
                                        <td className="py-2 px-2 text-right text-muted-foreground">{item.tax_rate || 0}%</td>
                                        <td className="py-2 pl-2 text-right font-semibold text-foreground">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.line_total || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export const VendorBillShow = () => (
    <Show title={<ShowTitle />} actions={<VendorBillActions />} disableBreadcrumb>
        <VendorBillShowContent />
    </Show>
);
